import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import '@/App.css';
import Sidebar from './components/Sidebar';
import Dashboard from './pages/Dashboard';
import Recommendations from './pages/Recommendations';
import Insights from './pages/Insights';
import Settings from './pages/Settings';
import ChatPage from './pages/ChatPage';
import ExportCenter from './pages/ExportCenter';
import PeriodAnalysis from './pages/PeriodAnalysis';
import EmployeeDeepDive from './pages/EmployeeDeepDive';
import ScenarioModeling from './pages/ScenarioModeling';
import InsightsActions from './pages/InsightsActions';
import LandingPage from './pages/LandingPage';
import AuthPage from './pages/AuthPage';
import AuthCallback from './components/AuthCallback';
import FloatingFilterPanel from './components/FloatingFilterPanel';
import { FilterProvider } from './context/FilterContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { Toaster } from './components/ui/sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AppContext = React.createContext({
  API,
  dimensions: { departments: [], locations: [], managers: [] },
  refreshDimensions: () => {},
  payTypes: [],
  premiumPayTypes: [],
  refreshPayTypes: () => {}
});

// Protected Route wrapper
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#F5F7FA] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1e3a5f]"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/auth" replace />;
  }

  return children;
};

// Main App Layout (with sidebar)
const AppLayout = ({ children }) => {
  return (
    <div className="flex min-h-screen">
      <Sidebar />
      <div className="flex-1 bg-[#F5F7FA]">
        {children}
      </div>
      <FloatingFilterPanel />
    </div>
  );
};

// Router with session_id detection
const AppRouter = () => {
  const location = useLocation();
  
  // Check URL for session_id from Google OAuth (hash or query param)
  // Safari on iPad sometimes handles redirects differently
  const hasSessionInHash = location.hash?.includes('session_id=');
  const hasSessionInQuery = location.search?.includes('session_id=');
  
  if (hasSessionInHash || hasSessionInQuery) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/auth" element={<AuthPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      
      {/* Protected routes */}
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <AppLayout>
            <Dashboard />
          </AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/insights-actions" element={
        <ProtectedRoute>
          <AppLayout>
            <InsightsActions />
          </AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/period-analysis" element={
        <ProtectedRoute>
          <AppLayout>
            <PeriodAnalysis />
          </AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/recommendations" element={
        <ProtectedRoute>
          <AppLayout>
            <Recommendations />
          </AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/insights" element={
        <ProtectedRoute>
          <AppLayout>
            <Insights />
          </AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/chat" element={
        <ProtectedRoute>
          <AppLayout>
            <ChatPage />
          </AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/export" element={
        <ProtectedRoute>
          <AppLayout>
            <ExportCenter />
          </AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/settings" element={
        <ProtectedRoute>
          <AppLayout>
            <Settings />
          </AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/employee/:employeeId" element={
        <ProtectedRoute>
          <AppLayout>
            <EmployeeDeepDive />
          </AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/scenario-modeling" element={
        <ProtectedRoute>
          <AppLayout>
            <ScenarioModeling />
          </AppLayout>
        </ProtectedRoute>
      } />
      
      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  const [dimensions, setDimensions] = useState({
    departments: [],
    locations: [],
    managers: []
  });
  const [payTypes, setPayTypes] = useState([]);

  const refreshDimensions = async () => {
    try {
      const response = await axios.get(`${API}/overtime/dimensions`);
      setDimensions(response.data);
    } catch (error) {
      console.error('Error fetching dimensions:', error);
    }
  };

  const refreshPayTypes = async () => {
    try {
      const response = await axios.get(`${API}/pay-types`);
      setPayTypes(response.data?.pay_types || []);
    } catch (error) {
      console.error('Error fetching pay types:', error);
    }
  };

  useEffect(() => {
    refreshDimensions();
    refreshPayTypes();
  }, []);

  const premiumPayTypes = payTypes.filter(pt => pt.premium_flag);

  return (
    <AuthProvider>
      <AppContext.Provider value={{
        API,
        dimensions,
        refreshDimensions,
        payTypes,
        premiumPayTypes,
        refreshPayTypes
      }}>
        <FilterProvider>
          <div className="App">
            <BrowserRouter>
              <AppRouter />
            </BrowserRouter>
            <Toaster />
          </div>
        </FilterProvider>
      </AppContext.Provider>
    </AuthProvider>
  );
}

export default App;